import { useState, FormEventHandler, useRef } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { Button } from 'react-scale/components';
import Spline from '@splinetool/react-spline';
import top from 'react-scale/public/static/summit/top.png';
import mid from 'react-scale/public/static/summit/mid.png';
import btm from 'react-scale/public/static/summit/btm.png';
import heroText from 'react-scale/public/static/summit/hero-text.svg';
import clsx from 'clsx';
import Portal from 'react-scale/components/primitives/portal';
import isologo from 'react-scale/public/static/summit/isologo.png';
import { useDeviceDetect } from 'react-scale/hooks/use-device-detect';
import logo from 'react-scale/public/static/summit/logo.svg';

export const AccessForm = ({ dates, place, subtitle }) => {
  const isologoRef = useRef<HTMLImageElement>();
  const curtainRef = useRef<HTMLDivElement>();
  const [error, setError] = useState(null);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const [formIsLoading, setFormIsLoading] = useState(false);
  const { isMobile } = useDeviceDetect();

  const handleAccess: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();
    // Prevent multiple submissions
    if (formIsLoading) return;

    setFormIsLoading(true);
    const formData = new FormData(e.currentTarget);
    const access = formData.get('access');

    if (!access) return;

    const res = await fetch('/api/summit/access', {
      method: 'POST',
      body: JSON.stringify({ access })
    });
    const json = await res.json();
    setFormIsLoading(false);

    if (res.status !== 200) return setError(json.error);

    window.location.reload();
  };

  return (
    <>
      {isMobile === false && (
        <figure
          className={clsx(
            'transition-opacity duration-300 delay-300',
            splineLoaded || '!opacity-0',
            'absolute top-0 left-0 pl-[33vw] w-screen h-screen !hidden md:!block overflow-hidden xl:scale-125'
          )}
        >
          <Spline
            className="relative"
            scene="https://prod.spline.design/3Q3ZCk-R1GYNBbCp/scene.splinecode"
            onLoad={() => setSplineLoaded(true)}
          />
        </figure>
      )}

      {!isMobile && (
        <Portal id="placeholder-models">
          <figure className="fixed top-0 left-0 hidden w-full h-full min-h-screen overflow-hidden pointer-events-none brightness-50 lg:block">
            <Image
              src={top}
              alt=""
              sizes="50vw, (min-width: 1024px) 20vw"
              className={clsx(
                'absolute w-auto h-[170px] left-[8vw] -rotate-12 -bottom-[2vh] md:h-[25vw] md:min-h-[450px] md:left-[42vw] 2xl:left-[51vw] md:-top-[17vh] md:-rotate-[26deg] md:bottom-[unset]  transition-opacity duration-300',
                splineLoaded && 'md:opacity-0'
              )}
            />
            <Image
              src={mid}
              alt=""
              sizes="50vw, (min-width: 1024px) 20vw"
              className={clsx(
                'absolute w-auto h-[170px] -right-[25vw] -scale-y-100 top-[2vh] -rotate-12 md:h-[195px] 2xl:h-[210px] md:scale-y-100 md:-right-8 md:top-[50vh] 2xl:right-[4vw] md:-rotate-6 2xl:-rotate-[10deg] 2xl:top-[40vh] transition-opacity duration-300',
                splineLoaded && 'md:opacity-0'
              )}
            />
            <Image
              src={btm}
              alt=""
              sizes="50vw, (min-width: 1024px) 20vw"
              className={clsx(
                'hidden md:block absolute w-auto h-[180px] right-[22vw] 2xl:right-[21vw] -rotate-[6deg] 2xl:top-[51vh] transition-opacity duration-300 top-[68vh]',
                splineLoaded && 'opacity-0'
              )}
            />
          </figure>
        </Portal>
      )}

      <div className="md:max-w-[510px] min-h-screen justify-center md:items-center flex flex-col z-10 relative pt-18 pb-12 xl:pt-[110px] xl:pb-12">
        <p className="flex flex-col md:flex-row uppercase gap-x-2 font-bold text-xl md:text-base xl:text-xl tracking-[3px] text-gradient-white xl:w-max text-center mx-auto">
          <span>{dates}</span>
          <span className="hidden md:inline">|</span>
          <span>{place}</span>
        </p>
        <h1 className="sr-only">Scale Summit</h1>
        <figure className="relative grow w-full md:w-[120%] max-h-[380px] min-h-[380px] md:max-h-[420px] xl:max-h-[520px] mb-9 md:!mb-6 mt-6">
          {isMobile === false && (
            <Spline
              onLoad={spline => {
                setTimeout(() => {
                  if (!isologoRef.current || !curtainRef.current) return;

                  isologoRef.current.style.opacity = '0';
                  curtainRef.current.style.opacity = '0';
                  spline.canvas.style.opacity = '1';
                }, 200);
              }}
              scene="https://prod.spline.design/N2XfoSUln-l-SVEf/scene.splinecode"
              className="absolute z-[9] transition-opacity duration-100 delay-100 -translate-x-1/2 -translate-y-1/2 opacity-0 top-1/2 left-1/2"
            />
          )}
          <Image
            priority
            src={heroText}
            alt="AI Security Summit"
            className="absolute top-4 w-9/12 md:w-[60%] sm:w-[70%] h-auto -translate-x-1/2 left-1/2 pointer-events-none sm:max-h-[250px] md:top-0 md:max-h-[220px] xl:top-0 xl:max-h-none"
          />
          <Image
            ref={isologoRef}
            src={isologo}
            alt=""
            className="absolute transition-opacity duration-100 w-full h-full md:w-[80%] left-1/2 -translate-x-1/2 bottom-0 object-contain"
            priority
            quality={100}
            sizes="100vw, (min-width: 1024px) 50vw"
            width={500}
          />

          <Image
            priority
            src={logo}
            alt="by Scale"
            className="z-50 absolute -bottom-4 md:-bottom-1 min-w-[120px] w-[20%] h-auto -translate-x-1/2 left-1/2 pointer-events-none"
          />
          <div
            ref={curtainRef}
            className="absolute bottom-0 w-full h-2/5 bg-gradient-to-t from-black to-transparent"
          />
        </figure>
        <p className="text-center mx-auto max-w-[410px] md:max-w-[80%] md:text-sm lg:text-base relative z-10">
          {subtitle}
        </p>
        <form className="relative mt-10 md:mx-auto" onSubmit={handleAccess}>
          <div className="flex flex-col gap-4 md:flex-row">
            <input
              name="access"
              type="password"
              required
              placeholder="Enter your access code"
              className="w-full text-center focus:outline focus:ring-0 focus:border-neutral-500 focus:outline-offset-3 focus:outline-1 focus:outline-neutral-500 md:text-left rounded-full placeholder:font-medium border-neutral-500 md:w-[296px] border bg-transparent px-5 py-2 placeholder:text-white"
            />
            {/* @ts-expect-error */}
            <Button
              isSubmit
              type="primary"
              centered={false}
              theme="dark"
              variant="grayscale"
              text="Submit"
              typeButton={{
                className:
                  'w-full md:w-auto !mx-0 border border-neutral-500 focus-visible:outline focus-visible:shadow-none focus-visible:ring-0 focus-visible:outline-offset-2 focus-visible:outline-1 focus-visible:outline-neutral-500 rounded-full'
              }}
            />
          </div>
          <p
            className={cn(
              'text-xs text-red-400 absolute -bottom-6 max-w-max ml-4 transition-opacity duration-300',
              error ? 'opacity-100' : 'opacity-0'
            )}
          >
            {error}
          </p>
        </form>
      </div>
    </>
  );
};
